import { detailedDiff } from 'deep-object-diff';
import unset from 'lodash-es/unset';

import { timeoutFetchSentryGuard } from 'Fetch';
import { postCheckoutOffer, updateEnquiryVehicleData } from 'PrivateAPI';
import { getMotorwayOffer, paasEnquiry } from 'PrivateApis';
import { postVrmCheck } from 'PublicAPI';

import { GA_VARIABLES, GTM_TRIGGERS } from 'Utilities/analytics';
import { BUYER_ID_LOOKUP, BUYER_SLUGS } from 'Utilities/buyers';
import dayjs from 'Utilities/dayjs';
import { isEmptyObject, isNil, isNull, isUndefined } from 'Utilities/helpers';
import type { Blocks, BlockType, PhotoStatusType, PremiumOffer, Seller, Slug, VehicleDetails } from 'Utilities/profiling/@types';
import { isPaasRestricted } from 'Utilities/restricted';
import type { VehicleLookupState } from 'Utilities/vehicles/@types';

import type { UlezVehicle } from '../contentPages/ulezPage/UlezPage.types';

import type { PhotoInput } from './Photos/ImageUpload/photosSections';
import { photoSections } from './Photos/ImageUpload/photosSections';
import {
  BEREAVEMENT_KEY, COMPANY_KEY, defaultKeysToRemove, ELECTRIC_FUEL_MAPPING, FAMILY_KEY, HYBRID_FUEL_MAPPING,
  IMAGE_RETAKE_STATUSES, KEEP_PRIVATE_PLATE, KEEPER_NAME, KEEPER_TYPE, LOCATION_FIELDS, MYSELF_KEY, NISSAN, NISSAN_LEAF,
  NO_VALUE_TEXT, OTHER, PRIVATE_PLATE_KEY, PROFILING_PATH, PROFILING_STATUSES, RENAULT, SELLING_VRM_STATUS,
  TICKET_TIERS, TOO_MANY_REQUESTS,
} from './VehicleProfiling.constants';

export const checkLocalAndServerInSync = (
  localData: Record<string, any>,
  serverData: Record<string, any>,
  keysToRemove: string[] = defaultKeysToRemove,
) => {
  keysToRemove.forEach((key) => {
    unset(localData, key);
    unset(serverData, key);
  });
  const { added, deleted, updated } = detailedDiff(localData, serverData);
  const isLocalDataOutOfSync = !isEmptyObject(added) || !isEmptyObject(deleted) || !isEmptyObject(updated);

  if (isLocalDataOutOfSync) {
    window?.Sentry?.captureException(new Error('Profiling form client out of sync with platform'), {
      extra: {
        added,
        deleted,
        updated,
      },
      level: 'warning',
      tags: {
        fetch: 'Post vehicle profiling data',
      },
    });
  }
};

export const postEnquiryVehicleData = (
  id: number | null | undefined,
  values: PremiumOffer,
  data: Partial<VehicleDetails>,
  cb?: () => void,
  errorCb?: ()=> void,
  keysToRemove?: string[],
) => {
  const result = updateEnquiryVehicleData(id, data)
    .then((res) => {
      if (res.status === 'success') {
        cb?.();
        checkLocalAndServerInSync({ ...values.vehicleDetails, ...data }, res.data, ['updatedAt', 'financeFigure', 'serviceHistory', 'plateHistory', ...(keysToRemove || [])]);
      }
      return res;
    })
    .catch((error) => {
      timeoutFetchSentryGuard(error, () => {
        window?.Sentry?.captureException(new Error(error));
        errorCb?.();
        throw new Error(error);
      });
    });

  return result;
};

export const dealershipPlatformPut = ({
  changedValues: { added, updated }, values: { id, vehicleDetails },
}: { changedValues: { added: Partial<PremiumOffer>; updated: Partial<PremiumOffer>}; values: PremiumOffer }) => {
  // We're using changedValues here because `hasNotWrittenOff` and `hasProofOfPurchase`
  // don't have initial values till they're added to the database,
  // so the updated value comes back as empty

  const postResults = (fields: Partial<PremiumOffer>) => {
    if (Object.keys(fields).length > 0) {
      updateEnquiryVehicleData(id, fields?.vehicleDetails ? { ...fields?.vehicleDetails } : { ...fields })
        .then((res) => {
          if (res.status === 'success') {
            checkLocalAndServerInSync({ ...vehicleDetails, ...fields?.vehicleDetails }, res.data);
          }
        })
        .catch((error) => timeoutFetchSentryGuard(error, () => window?.Sentry?.captureException(new Error(error))));
    }
  };

  postResults(added);
  postResults(updated);
};

export const isTouched = (v: any) => !isNull(v) && !isUndefined(v);

export const isOwnerFilled = (state?: VehicleDetails) => {
  const keeperTypeVal = state?.[KEEPER_TYPE];
  const keeperNameVal = state?.[KEEPER_NAME];
  if (keeperTypeVal === FAMILY_KEY
    || keeperTypeVal === BEREAVEMENT_KEY
    || keeperTypeVal === COMPANY_KEY
  ) {
    return isTouched(keeperNameVal) && Boolean(keeperNameVal?.length);
  }

  return isTouched(keeperTypeVal) && (keeperTypeVal !== OTHER && keeperTypeVal !== MYSELF_KEY);
};

export const isPrivatePlateFilled = (premiumOffer: PremiumOffer) => {
  const privatePlateVal = premiumOffer?.vehicleDetails?.[PRIVATE_PLATE_KEY];
  const keepPrivatePlateVal = premiumOffer?.vehicleDetails?.[KEEP_PRIVATE_PLATE];
  const sellingVRMVal = premiumOffer?.[SELLING_VRM_STATUS];

  return (isTouched(privatePlateVal) && !privatePlateVal)
  || Boolean(privatePlateVal && isTouched(keepPrivatePlateVal) && isTouched(sellingVRMVal));
};

export const IS_ELECTRIC = (vehicleLookup: VehicleLookupState | UlezVehicle) => ELECTRIC_FUEL_MAPPING
  .includes((vehicleLookup?.fuel ?? '').toLowerCase());
export const IS_HYBRID = (vehicleLookup: VehicleLookupState) => HYBRID_FUEL_MAPPING
  .includes((vehicleLookup?.fuel ?? '').toLowerCase());

interface GetSectionListProps {
  isElectric: boolean;
  isHybrid: boolean;
  mustHaveProofOfPurchase: boolean;
  showBatteryOwnership: boolean;
  text: any;
}

export const getSectionList = ({
  isElectric, isHybrid, mustHaveProofOfPurchase, showBatteryOwnership, text,
}: GetSectionListProps): Blocks => Object.entries<BlockType>(text).filter(([, { route }]: [string, any]) => {
  if (route?.startsWith?.('/electric-vehicle/battery')) {
    return isElectric && showBatteryOwnership;
  }

  if (route?.startsWith?.('/electric-vehicle')) {
    return isElectric || isHybrid;
  }

  if (route?.startsWith?.(PROFILING_PATH.PROOF_OF_PURCHASE)) {
    return mustHaveProofOfPurchase;
  }

  return route;
});

// TODO - Move into MW Tools
// Copied from https://github.com/motorway/motorway-dealer-webapp/blob/1895e2c60c7aaad23277214826abe39b8c0f9f1a/utilities/formatters.js?plain=1#L21-L44

export const formatText = (text: any) => {
  // eslint-disable-next-line no-restricted-globals
  if ((typeof text === 'string' || typeof text === 'number') && !isNaN(text as number)) {
    return Number(text).toLocaleString();
  }

  if (typeof text === 'string') {
    let t = text.toLowerCase().replace(/_/g, ' ').replace(/ and /g, ' & ').replace(/ *\{[^}]*?\} */g, ' ')
      .replace(/\.\s+([a-z]{1})|^\w/g, (c) => c.toUpperCase());
    const reg = /<\*(.*?)\*>/g;
    let match;
    // eslint-disable-next-line no-cond-assign
    while ((match = reg.exec(t))) {
      t = t.replace(match[0], match[1].toUpperCase());
    }

    return t;
  }
  if (typeof text === 'boolean') {
    return (text) ? 'Yes' : 'No';
  }
  if (typeof text === 'function') {
    return text();
  }
  if (Array.isArray(text)) {
    return text.join(', ');
  }
  if (text == null) {
    return NO_VALUE_TEXT;
  }

  return text;
};

export const isNotEmptyLocation = (data?: Seller) => Object.values(LOCATION_FIELDS).every((field) => {
  if (Array.isArray(field)) {
    return field.some((val) => data?.[val as keyof typeof data]);
  }

  return data?.[field];
});

export const isMustHaveProofOfPurchase = (keeperStartDate?: string | null) => dayjs().diff(dayjs(keeperStartDate), 'months') <= 3;

export const isRetakeStatus = (status: PhotoStatusType) => IMAGE_RETAKE_STATUSES.includes(status);

export const getPhotosGrid = (data = photoSections) => Object.keys(data)
  .reduce<PhotoInput[]>((acc, x) => [...acc, ...data[x].inputs || []], []);

export const PHOTOS_GRID = getPhotosGrid();

export const getSkippedPhotos = (data = photoSections) => Object.values(data).reduce<string[]>((acc, n) => {
  const skipped = n.inputs.filter(({ skip }) => skip).map(({ name }) => name);
  return [...acc, ...skipped];
}, []);

export const SKIPPED_PHOTOS = getSkippedPhotos();

export const isServiceResupply = (profilingStatus: PremiumOffer['profilingStatus']) => {
  if (!Array.isArray(profilingStatus)) {
    return false;
  }

  return profilingStatus.includes(PROFILING_STATUSES.SERVICE_HISTORY);
};

export const isPhotosResupply = (profilingStatus: PremiumOffer['profilingStatus']) => {
  if (!Array.isArray(profilingStatus)) {
    return false;
  }

  return profilingStatus.includes(PROFILING_STATUSES.PHOTOS_REQUESTED);
};

export const checkEnquiryIsGoldenTicket = (enquiryTier?: PremiumOffer['enquiryTier']) => enquiryTier === TICKET_TIERS.GOLDEN;
export const checkEnquiryIsSilverTicket = (enquiryTier: PremiumOffer['enquiryTier'] | null | undefined) => !isNil(enquiryTier) && enquiryTier === TICKET_TIERS.SILVER;

/**
 * Leased batteries only exist for original Nissan Leaf and some Renault Models
 */

export const isLeasedBatteryModel = (
  vehicle: VehicleLookupState,
): boolean => {
  const { make, model } = vehicle;

  if (make?.slug === RENAULT) {
    return true;
  }

  if (make?.slug === NISSAN && (model?.toLowerCase().includes(NISSAN_LEAF))) {
    return true;
  }

  return false;
};

export const checkEnquiryExpired = async (
  {
    dpEnquiryId,
    vrm,
  } : {
    dpEnquiryId?: number | null;
    vrm?: string;
  },
  shouldCheckVRM: boolean,
  isStatusTimeline: boolean,
  setError: React.Dispatch<React.SetStateAction<boolean | Record<string, any> | null>>,
) => {
  if (shouldCheckVRM && !isStatusTimeline) {
    try {
      const { enquiry } = await postVrmCheck(vrm);

      if (enquiry?.expired) {
        return true;
      }
    } catch (err: any) {
      timeoutFetchSentryGuard(err, () => window?.Sentry?.captureException?.(new Error(err), {
        extra: {
          dpEnquiryId,
          vrm,
        },
        level: 'error',
        tags: {
          fetch: 'Check VRM',
        },
      }));

      if (err.message === TOO_MANY_REQUESTS) {
        throw new Error(TOO_MANY_REQUESTS);
      }

      setError(true);
      return undefined;
    }
  }

  return false;
};

export const requestCheckoutOffer = async ({ enquiryId, vrm }: {
  enquiryId?: number | null;
  vrm: string;
}) => {
  const { valuation } = await getMotorwayOffer(vrm)
    .catch((err) => {
      window?.Sentry?.captureException?.(new Error(err), {
        level: 'warning',
      });
      return {};
    });

  if (valuation) {
    GA_VARIABLES.offerAmount = valuation?.valuation;

    // This is a temporary solution to change the vehicle status on new enquiries to `quote_accepted`
    // The back end will do this eventually and we can remove this
    postCheckoutOffer({
      buyerId: BUYER_ID_LOOKUP[BUYER_SLUGS.motorway],
      enquiryId,
      offer: { valuationId: valuation?.id },
    }).catch((err) => {
      window?.Sentry?.captureException?.(new Error(err), {
        level: 'warning',
      });
      return {};
    });
  }

  GTM_TRIGGERS.OFFER_SELECTED();
};

export const fetchPaasProfile = async (dpEnquiryId?: number | null, slug?: Slug | null, vrm?: string) => {
  if (!dpEnquiryId || !slug) {
    return false;
  }

  if (!isPaasRestricted(slug)) {
    return false;
  }

  return paasEnquiry(dpEnquiryId)
    .then((response) => !!response?.hasPaasProfile)
    .catch((err) => {
      timeoutFetchSentryGuard(err, () => window?.Sentry?.captureException?.(new Error(err), {
        extra: {
          dpEnquiryId,
          vrm,
        },
        level: 'error',
        tags: {
          fetch: 'Get paas enquiry data',
        },
      }));

      return false;
    });
};
